import axios from 'axios';

export type MetricEvent = {
  name: string;
  type: 'counter' | 'histogram';
  value: number;
  labels?: Record<string, string>;
  timestamp: number;
};

const METRICS_URL = `${process.env.REACT_APP_API_URL}/api/v1/metrics`;
const MAX_BUFFER_SIZE = 50;
const FLUSH_INTERVAL_MS = 5000;

const buffer: MetricEvent[] = [];
let idleCallbackHandle: number | null = null;

let initialized = false;

const initializeFlushHandlers = (): void => {
  if (initialized || typeof window === 'undefined') return;
  initialized = true;

  setInterval(flush, FLUSH_INTERVAL_MS);

  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'hidden' && buffer.length > 0) {
      const batch = buffer.splice(0);

      if (navigator.sendBeacon) {
        navigator.sendBeacon(
          METRICS_URL,
          new Blob([JSON.stringify({ metrics: batch })], { type: 'application/json' }),
        );
      }
    }
  });
};

const sendBatch = (batch: MetricEvent[]): void => {
  axios
    .post(METRICS_URL, { metrics: batch }, { withCredentials: true })
    .catch(() => undefined);
};

const flush = (): void => {
  if (buffer.length === 0) return;
  sendBatch(buffer.splice(0));
};

const scheduleIdleFlush = (): void => {
  if (idleCallbackHandle !== null) return;

  if (typeof window !== 'undefined' && 'requestIdleCallback' in window) {
    idleCallbackHandle = window.requestIdleCallback(
      () => {
        idleCallbackHandle = null;
        flush();
      },
      { timeout: FLUSH_INTERVAL_MS },
    );
  }
};

const enqueue = (event: MetricEvent): void => {
  initializeFlushHandlers();
  buffer.push(event);

  if (buffer.length >= MAX_BUFFER_SIZE) {
    flush();
    return;
  }

  scheduleIdleFlush();
};

export const metricsQueue = {
  increment(name: string, labels?: Record<string, string>): void {
    enqueue({ name, type: 'counter', value: 1, labels, timestamp: Date.now() });
  },
  timing(name: string, ms: number, labels?: Record<string, string>): void {
    enqueue({
      name,
      type: 'histogram',
      value: ms,
      labels,
      timestamp: Date.now(),
    });
  }
};
